import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Radar, Store, UserRound } from "lucide-react";
import logo from "assets/img/logo.png";

// Two-panel auth shell on the Duckflow design system (DESIGN_SYSTEM.md): the product's
// promise on a dark panel, the form on a light one. Below `lg` the dark panel goes away and
// the brand moves on top of the form.
export default function AuthSplit({ children, topRight }: { children: ReactNode; topRight?: ReactNode }) {
    return (
        <div className="flex min-h-screen w-full bg-surface font-sans">
            <BrandPanel />

            <main className="flex min-h-screen flex-1 flex-col px-4 py-6 md:px-8">
                <div className="flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2 lg:invisible" aria-label="DuckFlow">
                        <img src={logo} alt="" className="h-6 w-6" />
                        <span className="text-base font-semibold tracking-tight text-ink">DuckFlow</span>
                    </Link>
                    {topRight}
                </div>

                <div className="flex flex-1 items-center justify-center py-12">
                    <div className="w-full max-w-sm">{children}</div>
                </div>

                <footer className="text-center text-xs text-ink-faint">© {new Date().getFullYear()} DuckFlow</footer>
            </main>
        </div>
    );
}

function BrandPanel() {
    return (
        <aside className="relative hidden w-1/2 max-w-2xl flex-col justify-between overflow-hidden bg-surface-inverse px-12 py-10 lg:flex">
            <div className="flex items-center gap-2">
                <img src={logo} alt="" className="h-7 w-7 invert" />
                <span className="text-base font-semibold tracking-tight text-ink-inverse">DuckFlow</span>
            </div>

            <div className="space-y-10">
                <div className="space-y-5">
                    <h2 className="font-display text-4xl font-normal leading-tight tracking-tight text-ink-inverse xl:text-5xl">
                        Le bon restaurant,
                        <br />
                        au bon moment.
                    </h2>
                    <p className="max-w-md text-base leading-relaxed text-ink-inverse-muted">
                        Ouvertures, changements de gérant, franchises : DuckFlow repère les établissements prêts à
                        acheter et vous dit qui appeler, avant vos concurrents.
                    </p>
                </div>

                <SignalPreview />
            </div>

            {/* Mirrors the logo row so the content above sits on the panel's optical centre */}
            <div className="h-7" />
        </aside>
    );
}

// An illustration of the product's feed, not live data — hence generic names and no figures.
const PREVIEW_SIGNALS = [
    { icon: Store, type: "Ouverture", name: "Bistrot du Marché", place: "Lyon 2e", when: "il y a 2 h", highlight: true },
    { icon: UserRound, type: "Nouveau gérant", name: "Brasserie des Halles", place: "Bordeaux", when: "hier", highlight: false },
    { icon: CalendarClock, type: "Recrutement", name: "Le Comptoir Saint-Paul", place: "Paris 4e", when: "il y a 3 j", highlight: false },
];

function SignalPreview() {
    return (
        <div aria-hidden="true" className="rounded-2xl border border-border-inverse bg-white/[0.03] p-4">
            <div className="mb-3 flex items-center justify-between px-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-ink-inverse-muted">Vos signaux du jour</p>
                <span className="flex items-center gap-1.5 text-xs text-ink-inverse-muted">
                    <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-accent opacity-75 motion-safe:animate-ping" />
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
                    </span>
                    En direct
                </span>
            </div>
            <ul className="space-y-2">
                {PREVIEW_SIGNALS.map(({ icon: Icon, type, name, place, when, highlight }) => (
                    <li
                        key={name}
                        className={`flex items-center gap-3 rounded-lg border px-3 py-3 ${
                            highlight ? "border-accent/30 bg-accent-subtle-inverse" : "border-border-inverse bg-white/[0.03]"
                        }`}
                    >
                        <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${highlight ? "bg-accent text-white" : "bg-white/10 text-ink-inverse"}`}>
                            <Icon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-ink-inverse">{name}</p>
                            <p className="truncate text-xs text-ink-inverse-muted">
                                {type} · {place}
                            </p>
                        </div>
                        <span className="flex-shrink-0 text-xs text-ink-inverse-muted">{when}</span>
                    </li>
                ))}
            </ul>
            <p className="mt-3 flex items-center gap-1.5 px-1 text-xs text-ink-inverse-muted">
                <Radar className="h-3.5 w-3.5 text-accent" />
                Franchises, multipropriétaires, portefeuilles : tout le marché CHR, en détail.
            </p>
        </div>
    );
}
